import * as XLSX from "xlsx";
import {
    CheckCircleFilled,
    CheckCircleOutlined,
    CloseCircleFilled,
    DatabaseOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    FileExcelOutlined,
    InboxOutlined,
    LoadingOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Progress,
    Row,
    Space,
    Statistic,
    Steps,
    Table,
    Tag,
    Typography,
} from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../../variables/api/axiosMultiPartDatainstance";

const { Title, Text } = Typography;

// ─── Constantes ───────────────────────────────────────────────────────────────

const TYPE_LABELS  = { C: "Corporel", M: "Matériel", X: "Mixte" };
const TYPE_COLORS  = { C: "red", M: "orange", X: "purple" };
const ETAT_LABELS  = {
    EN_COURS: "En cours",
    TERMINE:  "Terminé",
    ERREUR:   "Erreur",
    INCONNU:  "Inconnu",
};

const POLL_INTERVAL_MS = 2500;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
    n == null ? "—" : new Intl.NumberFormat("fr-FR").format(n);

// ─── Colonnes échantillon (preview) ───────────────────────────────────────────

const echantiColonnes = [
    {
        title: "N° Sinistre (Pool)", dataIndex: "sin", key: "sin", width: 180, fixed: "left",
        render: v => <Text code style={{ fontSize: 11, fontWeight: 700 }}>{v || "—"}</Text>,
    },
    {
        title: "N° Police", dataIndex: "police", key: "police", width: 160,
        render: v => <Text code style={{ fontSize: 11 }}>{v || "—"}</Text>,
    },
    {
        title: "Immatriculation", dataIndex: "immat", key: "immat", width: 140,
        render: v => v ? <Text code style={{ fontSize: 11 }}>{v}</Text>
                      : <Text type="secondary">—</Text>,
    },
    {
        title: "Type", dataIndex: "type", key: "type", width: 100,
        render: v => v
            ? <Tag color={TYPE_COLORS[v] || "default"} style={{ fontSize: 10 }}>
                  {TYPE_LABELS[v] || v}
              </Tag>
            : <Text type="secondary">—</Text>,
    },
];

// ─── Colonnes lignes erreur (rapport) ────────────────────────────────────────

const errColonnes = [
    {
        title: "Ligne", dataIndex: "ligne", key: "ligne", width: 60,
        render: v => <Text type="secondary" style={{ fontSize: 11 }}>L{v}</Text>,
    },
    {
        title: "Message", dataIndex: "message", key: "message",
        render: v => <Text type="danger" style={{ fontSize: 12 }}>{v}</Text>,
    },
];

// ─── Composant principal ──────────────────────────────────────────────────────

export default function RepriseGumVt() {
    const [step,           setStep]           = useState(0);
    const [fichier,        setFichier]        = useState(null);     // File objet
    const [preview,        setPreview]        = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [rapport,        setRapport]        = useState(null);
    const [importLoading,  setImportLoading]  = useState(false);
    const [progress,       setProgress]       = useState(0);
    const [jobId,          setJobId]          = useState(null);
    const [jobEtat,        setJobEtat]        = useState(null);     // EN_COURS | TERMINE | ERREUR
    const pollRef = useRef(null);

    // ── Nettoyage polling au démontage ────────────────────────────────────────
    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    // ── Étape 1 : preview ─────────────────────────────────────────────────────
    const lancerPreview = async () => {
        if (!fichier) { toast.warning("Sélectionnez un fichier Excel."); return; }
        setPreviewLoading(true);
        setPreview(null);
        try {
            const form = new FormData();
            form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt/preview", form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setPreview(res.data);
            setStep(1);
        } catch (err) {
            toast.error("Erreur prévisualisation : " + (err.response?.data?.message || err.message));
        } finally {
            setPreviewLoading(false);
        }
    };

    // ── Étape 2 : import (async + polling) ────────────────────────────────────
    const lancerImport = async () => {
        if (!fichier) { toast.warning("Sélectionnez un fichier Excel."); return; }
        setImportLoading(true);
        setRapport(null);
        setProgress(5);
        setJobEtat("EN_COURS");
        setStep(2);

        try {
            const form = new FormData();
            form.append("fichier", fichier);
            const res = await axiosMultiPartDataInstance.post(
                "/api/migration/reprise-gumvt", form,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id);
            demarrerPolling(id);
        } catch (err) {
            setImportLoading(false);
            setJobEtat("ERREUR");
            toast.error("Erreur import : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            // Progression simulée : monte jusqu'à 90 % en attendant la réponse serveur
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100);
                    setJobEtat(statut.etat);
                    setImportLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat);
                        setStep(3);
                        toast.success("Import GUM/VT terminé !");
                    } else {
                        toast.error("Erreur import : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current);
                setImportLoading(false);
                setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    // ── Export rapport Excel ──────────────────────────────────────────────────
    const exportRapport = () => {
        if (!rapport) return;
        const wb = XLSX.utils.book_new();

        // Feuille Résumé
        const resume = [
            ["Indicateur", "Valeur"],
            ["Sinistres parsés",        rapport.totalParse],
            ["Importés avec succès",    rapport.succes],
            ["Déjà existants (skippés)",rapport.dejaExistants],
            ["Contrats force-créés",    rapport.forcesCreations],
            ["Erreurs",                 rapport.erreurs],
            ["Durée (ms)",              rapport.dureeMs],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");

        // Feuille Erreurs
        const errData = [["Ligne / Sinistre", "Message d'erreur"],
            ...(rapport.lignesErreur || []).map(e => [e, ""])];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(errData), "Erreurs");

        XLSX.writeFile(wb, `rapport_gumvt_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ── Réinitialiser ─────────────────────────────────────────────────────────
    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setFichier(null); setPreview(null);
        setRapport(null); setImportLoading(false);
        setProgress(0); setJobId(null); setJobEtat(null);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Rendu
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                {/* En-tête */}
                <Space align="center" style={{ marginBottom: 24 }}>
                    <DatabaseOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Reprise Sinistres GUM / VT
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Import du fichier Pool multi-feuilles — branches GUM (Moto) et VT (VC / VL)
                        </Text>
                    </div>
                </Space>

                {/* Étapes */}
                <Steps
                    current={step}
                    style={{ marginBottom: 32 }}
                    items={[
                        { title: "Sélection",  description: "Choisir le fichier Excel" },
                        { title: "Aperçu",     description: "Vérifier les données" },
                        { title: "Import",     description: "Traitement en cours" },
                        { title: "Rapport",    description: "Résultats" },
                    ]}
                />

                {/* ── Étape 0 : Sélection ────────────────────────────────── */}
                {step === 0 && (
                    <Card>
                        <Dragger
                            accept=".xlsx,.xls"
                            multiple={false}
                            beforeUpload={(f) => { setFichier(f); return false; }}
                            onRemove={() => setFichier(null)}
                            maxCount={1}
                            style={{ marginBottom: 24 }}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#242166" }} />
                            </p>
                            <p className="ant-upload-text">
                                Cliquez ou déposez le fichier Excel Pool GUM/VT ici
                            </p>
                            <p className="ant-upload-hint">
                                Fichier .xlsx multi-feuilles : Contrats, Sinistres, Provisions,
                                Règlements, Recours, Obs Sin
                            </p>
                        </Dragger>

                        {fichier && (
                            <Alert
                                type="info" showIcon
                                message={
                                    <Space>
                                        <FileExcelOutlined style={{ color: "#217346" }} />
                                        <Text strong>{fichier.name}</Text>
                                        <Text type="secondary">
                                            ({(fichier.size / 1024).toFixed(1)} Ko)
                                        </Text>
                                    </Space>
                                }
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <div style={{ textAlign: "right" }}>
                            <Button
                                type="primary"
                                size="large"
                                icon={<EyeOutlined />}
                                loading={previewLoading}
                                disabled={!fichier}
                                onClick={lancerPreview}
                                style={{ background: "#242166" }}
                            >
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {/* ── Étape 1 : Aperçu ───────────────────────────────────── */}
                {step === 1 && preview && (
                    <div>
                        {/* KPIs */}
                        <Card style={{ marginBottom: 16, borderLeft: "4px solid #242166" }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Sinistres"
                                        value={preview.totalSinistres}
                                        valueStyle={{ color: "#242166", fontSize: 22 }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Avec provisions"
                                        value={preview.totalAvecProvisions}
                                        valueStyle={{ color: "#0284c7", fontSize: 22 }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Avec règlements"
                                        value={preview.totalAvecReglements}
                                        valueStyle={{ color: "#16a34a", fontSize: 22 }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Avec recours"
                                        value={preview.totalAvecRecours}
                                        valueStyle={{ color: "#dc2626", fontSize: 22 }}
                                    />
                                </Col>
                            </Row>

                            <Divider style={{ margin: "16px 0" }} />

                            <Row gutter={[16, 8]}>
                                <Col xs={24} md={8}>
                                    <Descriptions size="small" column={1}>
                                        <Descriptions.Item label="Lignes provisions">
                                            <Text strong>{fmt(preview.totalProvisions)}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Lignes règlements">
                                            <Text strong>{fmt(preview.totalReglements)}</Text>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Descriptions size="small" column={1}>
                                        <Descriptions.Item label="Exercice min">
                                            <Tag color="blue">{preview.periodeMin || "—"}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Exercice max">
                                            <Tag color="blue">{preview.periodeMax || "—"}</Tag>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Alert
                                        type="info" showIcon
                                        message="Les données de la feuille Contrats seront utilisées pour créer les engins/contrats manquants (force-import)."
                                        style={{ fontSize: 11 }}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Anomalies de dates à corriger avant import */}
                        {preview.totalAnomaliesDates > 0 && (
                            <Card
                                size="small"
                                style={{ marginBottom: 16, borderLeft: "4px solid #f97316" }}
                                title={
                                    <Space>
                                        <WarningOutlined style={{ color: "#f97316" }} />
                                        <Text strong style={{ color: "#c2410c" }}>
                                            Dates illisibles — {preview.totalAnomaliesDates} cellule(s) à corriger
                                        </Text>
                                    </Space>
                                }
                            >
                                <Alert
                                    type="warning" showIcon
                                    style={{ marginBottom: 12 }}
                                    message="Ces cellules ont une date dans un format non reconnu (ex. année tronquée). Les sinistres concernés seront importés mais SANS cette date. Corrigez le fichier Excel puis relancez la prévisualisation."
                                />
                                <Table
                                    dataSource={preview.anomaliesDates.map((msg, i) => ({ key: i, ligne: i + 1, message: msg }))}
                                    columns={[
                                        { title: "#", dataIndex: "ligne", key: "ligne", width: 50,
                                          render: v => <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> },
                                        { title: "Cellule à corriger", dataIndex: "message", key: "message",
                                          render: v => <Text style={{ fontSize: 12 }}>{v}</Text> },
                                    ]}
                                    size="small"
                                    pagination={{ pageSize: 15, size: "small" }}
                                    scroll={{ y: 300 }}
                                />
                                {preview.totalAnomaliesDates > preview.anomaliesDates.length && (
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        … et {preview.totalAnomaliesDates - preview.anomaliesDates.length} autre(s)
                                        non affichée(s). Corrigez d'abord celles-ci.
                                    </Text>
                                )}
                            </Card>
                        )}

                        {/* Échantillon */}
                        {preview.echantillon?.length > 0 && (
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <DatabaseOutlined />
                                        <Text strong>Échantillon — 10 premiers sinistres</Text>
                                    </Space>
                                }
                                style={{ marginBottom: 16 }}
                            >
                                <Table
                                    dataSource={preview.echantillon.map((r, i) => ({ ...r, key: i }))}
                                    columns={echantiColonnes}
                                    size="small"
                                    pagination={false}
                                    scroll={{ x: 600 }}
                                />
                            </Card>
                        )}

                        <Space>
                            <Button onClick={() => setStep(0)}>
                                ← Retour
                            </Button>
                            <Button
                                type="primary"
                                size="large"
                                icon={<ThunderboltOutlined />}
                                onClick={lancerImport}
                                style={{ background: "#242166" }}
                            >
                                Lancer l'import
                            </Button>
                        </Space>
                    </div>
                )}

                {/* ── Étape 2 : Import en cours ──────────────────────────── */}
                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {importLoading ? (
                                <>
                                    <LoadingOutlined
                                        style={{ fontSize: 48, color: "#242166", marginBottom: 20 }}
                                    />
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 16 }}>
                                            Import GUM/VT en cours…
                                        </Text>
                                    </div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                Job ID : <Text code>{jobId}</Text>
                                            </Text>
                                            <Tag
                                                icon={<SyncOutlined spin />}
                                                color="processing"
                                                style={{ marginLeft: 8 }}
                                            >
                                                {ETAT_LABELS[jobEtat] || jobEtat}
                                            </Tag>
                                        </div>
                                    )}
                                    <Progress
                                        percent={progress}
                                        strokeColor="#242166"
                                        style={{ maxWidth: 500, margin: "0 auto" }}
                                    />
                                    <div style={{ marginTop: 12 }}>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            Le traitement peut prendre plusieurs minutes selon le
                                            volume de données (sinistres, provisions, règlements).
                                        </Text>
                                    </div>
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled
                                        style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }}
                                    />
                                    <div>
                                        <Text type="danger" strong style={{ fontSize: 16 }}>
                                            L'import a échoué.
                                        </Text>
                                    </div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>
                                        Recommencer
                                    </Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {/* ── Étape 3 : Rapport ──────────────────────────────────── */}
                {step === 3 && rapport && (
                    <div>
                        {/* Bannière résultat */}
                        <Alert
                            type={rapport.ok ? "success" : "warning"}
                            showIcon
                            icon={rapport.ok
                                ? <CheckCircleFilled style={{ color: "#52c41a" }} />
                                : <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                            }
                            message={rapport.ok
                                ? "Import terminé avec succès"
                                : `Import terminé avec ${rapport.erreurs} erreur(s)`
                            }
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s`}
                            style={{ marginBottom: 20 }}
                        />

                        {/* KPIs rapport */}
                        <Card style={{ marginBottom: 20 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={12} sm={4}>
                                    <Statistic
                                        title="Parsés"
                                        value={rapport.totalParse}
                                        valueStyle={{ fontSize: 20 }}
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <Statistic
                                        title="Importés"
                                        value={rapport.succes}
                                        valueStyle={{ color: "#52c41a", fontSize: 20 }}
                                        prefix={<CheckCircleFilled />}
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <Statistic
                                        title="Déjà existants"
                                        value={rapport.dejaExistants}
                                        valueStyle={{ color: "#8c8c8c", fontSize: 20 }}
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <Statistic
                                        title="Force-créations"
                                        value={rapport.forcesCreations}
                                        valueStyle={{ color: "#f97316", fontSize: 20 }}
                                        suffix={
                                            <Badge
                                                count="contrats"
                                                style={{ backgroundColor: "#f97316", fontSize: 9 }}
                                            />
                                        }
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <Statistic
                                        title="Erreurs"
                                        value={rapport.erreurs}
                                        valueStyle={{
                                            color: rapport.erreurs > 0 ? "#ff4d4f" : "#52c41a",
                                            fontSize: 20,
                                        }}
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <Statistic
                                        title="Durée"
                                        value={`${(rapport.dureeMs / 1000).toFixed(1)}s`}
                                        valueStyle={{ fontSize: 18 }}
                                    />
                                </Col>
                            </Row>
                        </Card>

                        {/* Détail des erreurs */}
                        {rapport.lignesErreur?.length > 0 && (
                            <Card
                                size="small"
                                title={
                                    <Space>
                                        <WarningOutlined style={{ color: "#ff4d4f" }} />
                                        <Text strong type="danger">
                                            Lignes en erreur ({rapport.lignesErreur.length})
                                        </Text>
                                    </Space>
                                }
                                style={{ marginBottom: 20 }}
                            >
                                <Table
                                    dataSource={rapport.lignesErreur.map((msg, i) => ({
                                        key: i,
                                        ligne: i + 1,
                                        message: msg,
                                    }))}
                                    columns={errColonnes}
                                    size="small"
                                    pagination={{ pageSize: 20, size: "small" }}
                                    scroll={{ x: 600, y: 300 }}
                                />
                            </Card>
                        )}

                        <Space>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={exportRapport}
                            >
                                Exporter rapport Excel
                            </Button>
                            <Button
                                type="primary"
                                icon={<RocketOutlined />}
                                onClick={reinitialiser}
                                style={{ background: "#242166" }}
                            >
                                Nouvel import
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
